import { CommonModule } from '@angular/common';
import { Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, convertToParamMap, ParamMap, Router } from '@angular/router';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { of, Subject, takeUntil } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  Address,
  AddressState,
  AdminUserType,
  Counties,
  Employee,
  EmployeeDetailService,
  HouseholdRecordVm,
  OwnerDetails,
  ProviderData,
  ProviderEnrollmentStatusModel,
  ProviderInfoStatus,
  ProviderTypeId,
  SchoolRecordVm,
  SubService,
  SvgIcon,
  toProviderTypeId,
  toProviderTypeLabel
} from '@k12/models';
import {
  AID_PROGRAM_ESA_PLUS,
  AID_PROGRAM_OPPORTUNITY_SCHOLARSHIP,
  parseAidProgramEligibility
} from '../aid-program-eligibility';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { StatusBadgeComponent } from '@k12/ui-primitives';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { provideNgxMask } from 'ngx-mask';
import { State, states } from '@k12/forms';
import { PersonalInformationComponent } from './personal-information/personal-information.component';
import { ProviderDetailsComponent } from './provider-details/provider-details.component';
import { BusinessAddressComponent } from './business-address/business-address.component';
import { PersonalAddressComponent } from './personal-address/personal-address.component';
import { LicenseInfoComponent } from './license-info/license-info.component';
import { TutorPersonalAddressComponent } from './tutor-personal-address/tutor-personal-address.component';
import { SchoolInfoComponent } from './school-info/school-info.component';
import { HdmaInformationComponent } from './hdma-information/hdma-information.component';
import { SchoolDetailsComponent } from './school-details/school-details.component';
import { SchoolAddressComponent } from './school-address/school-address.component';
import { HouseholdInfoComponent } from './household-info/household-info.component';
import { HouseholdContactInfoComponent } from './household-contact-info/household-contact-info.component';
import { HouseholdParentInfoComponent } from './household-parent-info/household-parent-info.component';
import { HouseholdFinancialInfoComponent } from './household-financial-info/household-financial-info.component';
import { BasicInformationComponent } from './basic-information/basic-information.component';
import { ProviderInfoComponent } from './provider-info/provider-info.component';
import {
  zipCodeValidator,
  nameValidator,
  phoneValidator,
  noSpaceEmailValidator,
  feinValidator,
  pastDateValidator,
  noWhitespaceValidator,
  tenDigitNumberValidator,
  numericOrDecimalValidator,
  ssnDisplayValidator
} from '@k12/validators';
import { ProviderService } from '@k12/app-services';
import { resolveSsnForSubmit } from '@k12/directives';

/**
 * Sentinel for the tri-state residency selects. Angular Material cannot render a `null` option
 * value, so "Not Evaluated" round-trips through this string and maps back to `null` on save.
 */
const NOT_EVALUATED = 'not-evaluated';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-user-info-panel',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    StatusBadgeComponent,
    PersonalInformationComponent,
    ProviderDetailsComponent,
    BusinessAddressComponent,
    PersonalAddressComponent,
    SchoolInfoComponent,
    LicenseInfoComponent,
    TutorPersonalAddressComponent,
    HdmaInformationComponent,
    SchoolDetailsComponent,
    SchoolAddressComponent,
    HouseholdInfoComponent,
    HouseholdContactInfoComponent,
    HouseholdParentInfoComponent,
    HouseholdFinancialInfoComponent,
    BasicInformationComponent,
    ProviderInfoComponent
  ],
  providers: [provideNgxMask()],
  templateUrl: './user-info-panel.component.html',
  styleUrl: './user-info-panel.component.scss'
})
export class UserInfoPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userData: ProviderData | SchoolRecordVm | HouseholdRecordVm | null = null;
  @Input() enableEdit: boolean = false;
  @Input() isEditing: boolean = false;
  @Input() subServices: SubService[] = [];
  @Input() isLoading: boolean = false;
  @Input() userType: AdminUserType = AdminUserType.Provider;
  @Input() currentUserType: AdminUserType = AdminUserType.All;
  @Input() schoolStatus: ProviderInfoStatus = ProviderInfoStatus.OnHold;
  @Input() schoolRestrictionMessage: string | null = null;
  /** When false, hides enrollment / verified status badges for provider users (e.g. partner app). */
  @Input() showReviewStatus: boolean = true;
  /** Loads tutor school dropdown with id/code (e.g. provider mypage EmployeeService). */
  @Input() employeeDetailService?: EmployeeDetailService;
  /** Determines if the edit button is visible. Defaults to true. */
  @Input() canEdit: boolean = true;

  @Output() edit: EventEmitter<void> = new EventEmitter<void>();
  @Output() save: EventEmitter<HouseholdRecordVm | ProviderData | SchoolRecordVm> = new EventEmitter<
    HouseholdRecordVm | ProviderData | SchoolRecordVm
  >();
  @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();
  /** Emitted when provider `type` (Tutor / Therapy / …) changes in the form so parents can reload subservices. */
  @Output() providerServiceTypeChange = new EventEmitter<ProviderData['type'] | null>();

  readonly blueCheckIcon: SvgIcon = SvgIcon.BlueCheck;
  readonly editIcon: SvgIcon = SvgIcon.Edit;
  readonly saveIcon: SvgIcon = SvgIcon.FloppyDisk;
  public readonly usStates = states;

  detailsExpanded: boolean = true;
  enrollmentStatus: ProviderInfoStatus | null = null;
  providerEnrollmentStatuses: ProviderEnrollmentStatusModel[] = [];
  verifiedStatus = ProviderInfoStatus.Verified;
  public states: State[] = states;
  public counties: { value: string; name: string }[] = Object.entries(Counties).map(([, value]) => ({
    value: value,
    name: value
  }));

  providerData!: ProviderData;
  schoolData!: SchoolRecordVm;
  householdData!: HouseholdRecordVm;

  providerForm!: FormGroup;
  ProviderInfoStatus = ProviderInfoStatus;
  schoolForm!: FormGroup;
  householdForm!: FormGroup;
  readonly adminUserTypes = AdminUserType;
  readonly NPI2EmployeeThreshold: number = 2;
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroy$ = new Subject<void>();
  private editableFieldsAllowlist: Set<string> | null = null;
  private static readonly ALWAYS_READ_ONLY_PROVIDER_FIELDS = new Set(['email', 'businessEmail', 'type']);
  private readonly providerService = inject(ProviderService);

  // School dropdown options
  aidProgramOptions: string[] = [AID_PROGRAM_ESA_PLUS, AID_PROGRAM_OPPORTUNITY_SCHOLARSHIP];

  get firstName() {
    return this.providerForm.get('firstName');
  }

  get middleName() {
    return this.providerForm.get('middleName');
  }

  get lastName() {
    return this.providerForm.get('lastName');
  }

  get ownerDetails(): OwnerDetails | null {
    return this.providerData?.ownerDetails || null;
  }

  get providerType(): ProviderData['type'] | undefined {
    if (this.userType !== AdminUserType.Provider) {
      return undefined;
    }
    const formType = this.providerForm?.get('type')?.value as ProviderData['type'] | null | undefined;
    // Prefer an explicit form selection while editing; fall back when empty (disabled control may not patch).
    if (this.isEditing && formType) {
      return formType;
    }
    return this.providerData?.type;
  }

  get isTherapyType(): boolean {
    return this.providerType === 'Therapy';
  }

  get isTutorType(): boolean {
    return this.providerType === 'Tutor';
  }

  get isTransportationType(): boolean {
    return this.providerType === 'Transportation';
  }

  get providerTypeLabel(): string {
    return toProviderTypeLabel(this.providerType ?? this.providerData?.type);
  }

  private revealAllValidationErrors(form: FormGroup | null | undefined): void {
    if (!form) return;
    form.markAllAsTouched();
    form.markAsDirty();
    form.updateValueAndValidity({ emitEvent: false });
  }

  private activeEditForm(): FormGroup {
    if (this.userType === AdminUserType.Provider) return this.providerForm;
    if (this.userType === AdminUserType.Schools) return this.schoolForm;
    return this.householdForm;
  }

  isActiveFormInvalid(): boolean {
    if (!this.isEditing) return false;
    const form = this.activeEditForm();
    return !!form?.invalid;
  }

  getValidationSummary(): { path: string; message: string }[] {
    const form = this.activeEditForm();
    if (!form) return [];
    return this.findInvalidFieldErrors(form);
  }

  private findInvalidFieldErrors(control: AbstractControl, fieldLabel = ''): { path: string; message: string }[] {
    if (control.disabled) return [];

    if (control instanceof FormGroup) {
      return Object.entries(control.controls).flatMap(([key, child]) =>
        this.findInvalidFieldErrors(
          child,
          fieldLabel ? `${fieldLabel} → ${this.getFieldLabel(key)}` : this.getFieldLabel(key)
        )
      );
    }

    if (control instanceof FormArray) {
      return control.controls.flatMap((child, i) => this.findInvalidFieldErrors(child, `${fieldLabel} [${i + 1}]`));
    }

    if (control.invalid && control.errors) {
      return [
        {
          path: fieldLabel || 'Form',
          message: this.formatValidationErrors(control.errors)
        }
      ];
    }

    return [];
  }

  private getFieldLabel(key: string): string {
    const withSpaces = key.replace(/([a-z])([A-Z])/g, '$1 $2');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  }

  private notifyParentWhenServiceTypeChanges(): void {
    const typeControl = this.providerForm?.get('type');
    if (!typeControl) return;

    typeControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value: ProviderData['type'] | null) => {
        this.providerServiceTypeChange.emit(value);
        this.applyTherapyFieldRules();
      });
  }

  private isTherapyProvider(): boolean {
    return this.isTherapyType;
  }

  private isTransportationProvider(): boolean {
    return this.isTransportationType;
  }

  private persistLicenseFields(type?: string | null): boolean {
    const providerTypeId = toProviderTypeId(type ?? this.providerType ?? this.providerData?.type);
    return providerTypeId === ProviderTypeId.Therapy || providerTypeId === ProviderTypeId.Transportation;
  }

  private getMyEmployeesCount(employees?: Employee[] | null): number {
    if (!employees?.length) {
      return 0;
    }

    return employees.filter((employee) => employee != null).length;
  }

  /**
   * Therapy providers require NPI and license number.
   * Transportation providers require business license (no NPI).
   * Tutors do not collect or persist license number / issuing state.
   * NPI2 is required (and enabled) only when employee count meets the threshold for Therapy.
   */
  private applyTherapyFieldRules(): void {
    const isTherapy = this.isTherapyProvider();
    const persistLicense = this.persistLicenseFields();
    const requireNpi2 =
      isTherapy && this.getMyEmployeesCount(this.providerData?.employees) >= this.NPI2EmployeeThreshold;

    this.applyRequiredFieldRule('npiNumber', isTherapy, { enableWhenRequired: true });
    this.applyRequiredFieldRule('licenseNumber', persistLicense, {
      enableWhenRequired: true,
      disableWhenOptional: true
    });
    this.applyRequiredFieldRule('issuingState', persistLicense, {
      enableWhenRequired: true,
      disableWhenOptional: true
    });
    this.applyRequiredFieldRule('npiNumber2', requireNpi2, {
      enableWhenRequired: true,
      disableWhenOptional: true
    });
    this.applySubjectsFieldRules(isTherapy);
    this.applyEditableFieldsAllowlist();
  }

  /** Therapy uses a single subject string; Tutor keeps a multi-select list. */
  private applySubjectsFieldRules(isTherapy: boolean): void {
    const control = this.providerForm?.get('subjects');
    if (!control) {
      return;
    }

    const names = this.normalizeSubjectNames(control.value);
    control.setValue(isTherapy ? (names[0] ?? '') : names, { emitEvent: false });
    this.applyRequiredFieldRule('subjects', isTherapy);
  }

  private normalizeSubjectNames(value: unknown): string[] {
    const items = Array.isArray(value) ? value : [value];
    return items.map((item) => String(item ?? '').trim()).filter(Boolean);
  }

  private applyRequiredFieldRule(
    controlName: string,
    required: boolean,
    options?: { enableWhenRequired?: boolean; disableWhenOptional?: boolean }
  ): void {
    const control = this.providerForm?.get(controlName);
    if (!control) {
      return;
    }

    if (required) {
      if (options?.enableWhenRequired && this.isFieldEditable(controlName)) {
        control.enable({ emitEvent: false });
      }
      control.setValidators([Validators.required]);
      if (controlName === 'npiNumber' || controlName === 'npiNumber2') {
        control.addValidators(tenDigitNumberValidator());
      }
    } else {
      control.clearValidators();
      control.setErrors(null);
      if (options?.disableWhenOptional) {
        control.disable({ emitEvent: false });
      }
    }

    control.updateValueAndValidity({ emitEvent: false });
    if(controlName === 'issuingState' || controlName === 'licenseNumber' || controlName === 'subjects') {
        control.addValidators(noWhitespaceValidator());
      }
  }

  public isFieldEditable(fieldName: string): boolean {
    if (UserInfoPanelComponent.ALWAYS_READ_ONLY_PROVIDER_FIELDS.has(fieldName)) {
      return false;
    }
    if (!this.editableFieldsAllowlist) {
      return true;
    }
    return this.editableFieldsAllowlist.has(fieldName);
  }

  private parseEditableFieldsAllowlist(raw: string | null): void {
    if (!raw?.trim()) {
      this.editableFieldsAllowlist = null;
      return;
    }
    this.editableFieldsAllowlist = new Set(
      raw
        .split(',')
        .map((field) => field.trim())
        .filter(Boolean)
    );
  }

  private applyEditableFieldsAllowlist(): void {
    if (!this.providerForm || !this.editableFieldsAllowlist) {
      return;
    }

    for (const fieldName of Object.keys(this.providerForm.controls)) {
      const control = this.providerForm.get(fieldName);
      if (!control) {
        continue;
      }
      if (this.isFieldEditable(fieldName)) {
        if (control.disabled) {
          control.enable({ emitEvent: false });
        }
      } else if (control.enabled) {
        control.disable({ emitEvent: false });
      }
    }
  }

  private shouldDisableProviderField(fieldName: string, reviewGated = false): boolean {
    if (UserInfoPanelComponent.ALWAYS_READ_ONLY_PROVIDER_FIELDS.has(fieldName)) {
      return true;
    }
    if (this.editableFieldsAllowlist) {
      return !this.editableFieldsAllowlist.has(fieldName);
    }
    return reviewGated && this.showReviewStatus;
  }

  onInstitutionClick(): void {
    const institutionId = this.schoolData?.institutionId;
    if (!institutionId) {
      console.warn('Cannot navigate to edit Institution: missing institutionId');
      return;
    }
    this.router.navigate(['/institutions/institution', institutionId, 'edit'], { 
      queryParams: { partnerTypeId: 1 }
    });
  }

  /** Partner Services select: clear selections that are not in the current subservices list. */
  private trimInvalidSubjectSelections(): void {
    if (this.showReviewStatus || !this.providerForm) return;

    const subjectsControl = this.providerForm.get('subjects');
    if (!subjectsControl) return;

    const allowedNames = new Set((this.subServices ?? []).flatMap((service) => (service?.name ? [service.name] : [])));
    const selected = this.normalizeSubjectNames(subjectsControl.value);
    const stillValid = selected.filter((name) => allowedNames.has(name));
    if (stillValid.length === selected.length) return;

    const nextValue = this.isTherapyProvider() ? (stillValid[0] ?? '') : stillValid;
    subjectsControl.setValue(nextValue, { emitEvent: false });
  }

  private formatValidationErrors(errors: ValidationErrors): string {
    return Object.entries(errors)
      .map(([key, val]) => {
        if (val === true || val === undefined) {
          return this.validationErrorLabel(key);
        }
        if (typeof val === 'object' && val !== null && 'message' in val) {
          return String((val as { message: unknown }).message);
        }
        if (typeof val === 'object' && val !== null) {
          return `${this.validationErrorLabel(key)} (${JSON.stringify(val)})`;
        }
        return `${this.validationErrorLabel(key)}: ${String(val)}`;
      })
      .join('; ');
  }

  private validationErrorLabel(key: string): string {
    if (key === 'required') return 'This field is required';
    if (key === 'invalidPhone') return 'Invalid phone number';
    if (key === 'invalidZipCode') return 'Invalid zip code';
    if (key === 'invalidSSN') return 'Invalid SSN';
    return key;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subServices']) {
      this.subServices = changes['subServices'].currentValue ?? [];
      this.trimInvalidSubjectSelections();
    }
    this.onChangeUserData(changes);
    if (changes['isEditing']?.currentValue) {
      if (this.userType === AdminUserType.Provider) {
        this.updateFormValues();
        this.applyTherapyFieldRules();
      }
      if (this.userType === AdminUserType.Schools) {
        this.updateSchoolFormValues();
      }
      if (this.userType === AdminUserType.Households) {
        this.updateHouseholdFormValues();
      }

      // Entering edit mode: immediately reveal all validation errors on pre-filled data.
      this.revealAllValidationErrors(this.activeEditForm());
    }
  }

  private onChangeUserData(changes: SimpleChanges): void {
    if (changes['userData']) {
      this.userData = changes['userData'].currentValue;
      this.initializeForms();
      if (this.userType === AdminUserType.Households) {
        this.householdData = this.userData as HouseholdRecordVm;
        this.updateHouseholdFormValues();
      }
      if (this.userType === AdminUserType.Provider) {
        this.providerData = this.userData as ProviderData;
        this.updateFormValues();
        this.applyTherapyFieldRules();
        this.enrollmentStatus = this.providerService.setEnrollmentStatus(this.providerData?.enrollmentStatus ?? '');
      }
      if (this.userType === AdminUserType.Schools) {
        this.schoolData = this.userData as SchoolRecordVm;
        this.updateSchoolFormValues();
      }

      // If we're already editing and data loads/changes, show all invalid fields immediately.
      if (this.isEditing) {
        //this.revealAllValidationErrors(this.activeEditForm());
      }
    }
  }

  ngOnInit(): void {
    (this.route.queryParamMap ?? of(convertToParamMap({})))
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        this.parseEditableFieldsAllowlist(params.get('editableFields'));
        if (this.providerForm) {
          this.applyTherapyFieldRules();
        }
      });

    this.initializeForms();
    this.applyTherapyFieldRules();
    this.applyCountyValidation(this.providerForm, 'state', 'county');
    this.applyCountyValidation(this.providerForm, 'personalState', 'personalCounty');
    this.applyCountyValidation(this.schoolForm, 'schoolState', 'schoolCounty');
    this.applyCountyValidation(this.householdForm, 'state', 'county');
    this.notifyParentWhenServiceTypeChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeForms(): void {
    if (!this.providerForm) {
      this.providerForm = this.createProviderForm();
    }
    if (!this.schoolForm) {
      this.schoolForm = this.createSchoolForm();
    }
    if (!this.householdForm) {
      this.householdForm = this.createHouseholdForm();
    }
  }

  private createProviderForm(): FormGroup {
    const reviewDisabled = this.showReviewStatus;
    const field = (name: string, value: unknown = '', reviewGated = false) => [
      { value, disabled: this.shouldDisableProviderField(name, reviewGated) }
    ];
    const fieldWithValidators = (
      name: string,
      value: unknown,
      validators: Parameters<FormBuilder['control']>[1],
      reviewGated = false
    ) => [{ value, disabled: this.shouldDisableProviderField(name, reviewGated) }, validators];

    return this.fb.group({
      firstName: fieldWithValidators('firstName', '', [Validators.required, nameValidator()]),
      middleName: fieldWithValidators('middleName', '', nameValidator()),
      lastName: fieldWithValidators('lastName', '', [Validators.required, nameValidator()]),
      phone: fieldWithValidators('phone', '', [Validators.required, phoneValidator()]),
      secondaryPhone: fieldWithValidators('secondaryPhone', '', [phoneValidator()]),
      address1: fieldWithValidators('address1', '', [Validators.required, noWhitespaceValidator()]),
      city: fieldWithValidators('city', '', [Validators.required, noWhitespaceValidator()]),
      state: fieldWithValidators('state', '', [Validators.required, noWhitespaceValidator()]),
      zip: fieldWithValidators('zip', '', [Validators.required, zipCodeValidator()]),
      county: fieldWithValidators('county', '', [noWhitespaceValidator()]),
      businessName: fieldWithValidators(
        'businessName',
        '',
        reviewDisabled ? [] : [Validators.required, noWhitespaceValidator()]
      ),
      businessPhone: fieldWithValidators(
        'businessPhone',
        '',
        reviewDisabled ? [phoneValidator()] : [Validators.required, phoneValidator()]
      ),
      businessSecondaryPhone: fieldWithValidators('businessSecondaryPhone', '', [phoneValidator()]),
      businessSameAsPersonal: field('businessSameAsPersonal', false),
      businessEmail: fieldWithValidators('businessEmail', '', [noSpaceEmailValidator()]),
      email: fieldWithValidators('email', '', [noSpaceEmailValidator()]),
      graduationDate: field('graduationDate', null, true),
      personalAddress1: fieldWithValidators('personalAddress1', '', [noWhitespaceValidator()], true),
      personalAddress2: field('personalAddress2', '', true),
      personalCity: fieldWithValidators('personalCity', '', [noWhitespaceValidator()], true),
      personalState: fieldWithValidators('personalState', '', [noWhitespaceValidator()], true),
      personalZip: fieldWithValidators('personalZip', '', [zipCodeValidator()], true),
      personalCounty: fieldWithValidators('personalCounty', '', [noWhitespaceValidator()], true),
      birthDate: fieldWithValidators('birthDate', null, pastDateValidator(), true),
      licenseNumber: fieldWithValidators('licenseNumber', '', [noWhitespaceValidator()], true),
      npiNumber: fieldWithValidators('npiNumber', '', [tenDigitNumberValidator()], true),
      npiNumber2: fieldWithValidators('npiNumber2', '', [tenDigitNumberValidator()], true),
      licenseType: fieldWithValidators('licenseType', '', [noWhitespaceValidator()], true),
      therapyLicenseTypeId: field('therapyLicenseTypeId', 1, true),
      issuingState: fieldWithValidators('issuingState', '', [noWhitespaceValidator()], true),
      schoolId: fieldWithValidators('schoolId', '', [noWhitespaceValidator()], true),
      schoolCode: fieldWithValidators('schoolCode', '', [noWhitespaceValidator()], true),
      schoolName: fieldWithValidators('schoolName', '', [noWhitespaceValidator()], true),
      socialSecurityLast4: fieldWithValidators('socialSecurityLast4','',
        [Validators.required, ssnDisplayValidator()],
        false
      ),
      /** Therapy vs Tutor (basic-information; partner portal when showReviewStatus is false). */
      type: fieldWithValidators('type', '' as ProviderData['type'], [Validators.required, noWhitespaceValidator()], true),
      /** Therapy / Tutor subservices / subjects */
      subjects: fieldWithValidators('subjects', [] as string[], [], true),
      providerEnrollmentStatusId: field('providerEnrollmentStatusId', null as number | null),
    });
  }

  private createSchoolForm(): FormGroup {
    return this.fb.group({
      // HDMA Information
      hdmaFirstName: ['', [Validators.required, nameValidator()]],
      hdmaMiddleName: ['', nameValidator()],
      hdmaLastName: ['', [Validators.required, nameValidator()]],
      hdmaEmail: [{ value: '', disabled: true }, [Validators.email, noSpaceEmailValidator()]],

      // Editable fields
      schoolPhone: ['', [Validators.required, phoneValidator(), noWhitespaceValidator()]],
      schoolPhoneExt: ['', [Validators.maxLength(4), noWhitespaceValidator()]],
      backgroundCheckValidationDate: [
        '',
        [Validators.required, pastDateValidator({ allowToday: true }), noWhitespaceValidator()]
      ],

      // School Details
      schoolId: [{ value: '', disabled: true }, [noWhitespaceValidator()]],
      schoolName: ['', [Validators.required, noWhitespaceValidator()]],
      schoolStatusId: [null as number | null, Validators.required],
      fiscalYearEndDate: ['', [Validators.required, noWhitespaceValidator()]],
      educationDeliveryMethod: [[], Validators.required],
      aidProgramEligibility: [[], Validators.required],
      feinNumber: ['', [Validators.required, feinValidator()]],
      dnpeNumber: ['', [Validators.required, noWhitespaceValidator()]],
      // Optional: most schools have no type yet, and that must not block saving other edits.
      schoolTypeId: [null as number | null],

      // School Address
      schoolAddress1: ['', [Validators.required, noWhitespaceValidator()]],
      schoolAddress2: ['', [noWhitespaceValidator()]],
      schoolCity: ['', [Validators.required, noWhitespaceValidator()]],
      schoolState: ['', [Validators.required, noWhitespaceValidator()]],
      schoolCounty: ['', [noWhitespaceValidator()]],
      schoolZip: ['', [Validators.required, zipCodeValidator()]]
    });
  }

  private createHouseholdForm(): FormGroup {
    const householdDisabled = this.currentUserType === AdminUserType.Households;
    const householdField = (value: unknown, validators?: Parameters<FormBuilder['control']>[1]) =>
      validators == null
        ? [{ value, disabled: householdDisabled }]
        : [{ value, disabled: householdDisabled }, validators];

    return this.fb.group({
      // Contact Information — the household maintains its own address, so these stay editable in the portal.
      address1: ['', [Validators.required, noWhitespaceValidator()]],
      address2: ['', [noWhitespaceValidator()]],
      city: ['', [Validators.required, noWhitespaceValidator()]],
      state: ['', [Validators.required, noWhitespaceValidator()]],
      zip: ['', [Validators.required, zipCodeValidator()]],
      county: ['', [noWhitespaceValidator()]],
      phone: ['', [Validators.required, phoneValidator()]],
      email: [{ value: '', disabled: true }, [Validators.email, noSpaceEmailValidator()]],

      // Parent Information
      firstName: householdField('', [Validators.required, nameValidator()]),
      middleName: householdField('', [nameValidator()]),
      lastName: householdField('', [Validators.required, nameValidator()]),
      birthDate: householdField('', [Validators.required, pastDateValidator()]),
      ssn: householdField('', [Validators.required, ssnDisplayValidator()]),

      // Household Information
      householdIncome: [null, [numericOrDecimalValidator()]],
      householdMemberCount: [null, [numericOrDecimalValidator()]],
      householdIncomeVerification: [{ value: false, disabled: householdDisabled }],
      householdNcResidency: [{ value: NOT_EVALUATED, disabled: householdDisabled }],
      householdNcResidencyVerified: [{ value: NOT_EVALUATED, disabled: householdDisabled }],

      // Read-only fields
      pidNumber: [{ value: '', disabled: true }],
      householdTier: [{ value: '', disabled: true }],
      // Owned by the Income Verification process, never editable here, and excluded from the
      // save payload so an admin edit cannot overwrite the verified figure.
      verifiedIncome: [{ value: '', disabled: true }]
    });
  }

  updateFormValues(): void {
    if (!this.providerForm || !this.providerData) return;

    // Enable therapy license fields before patching; disabled controls drop patchValue.
    this.applyTherapyFieldRules();

    const phone = this.showReviewStatus
      ? this.normalizeUsPhoneFormatting(this.providerData.phone)
      : this.normalizeUsPhoneFormatting(this.ownerDetails?.phone);
    const secondaryPhone = this.showReviewStatus
      ? this.normalizeUsPhoneFormatting(this.providerData.secondaryPhone ?? this.providerData.phonePrimary)
      : this.normalizeUsPhoneFormatting(this.ownerDetails?.secondaryPhone);

    this.providerForm.patchValue({
      firstName: this.providerData.firstName || '',
      middleName: this.providerData.middleName || '',
      lastName: this.providerData.lastName || '',
      businessName: this.providerData.businessName || '',
      email: this.providerData.email || '',
      businessEmail: this.providerData.businessEmail || '',
      phone: phone || '',
      secondaryPhone: secondaryPhone || '',
      address1: this.providerData.addresses?.[0]?.address1 || '',
      address2: this.providerData.addresses?.[0]?.address2 || '',
      city: this.providerData.addresses?.[0]?.city || '',
      state: this.getSelectedBusinessAddressState() || '',
      zip: this.providerData.addresses?.[0]?.zip || '',
      county: this.providerData.addresses?.[0]?.county || '',
      type: this.providerData.type ?? null,
      subjects: this.subjectsValueForForm(this.providerData.subjects)
    });
    // `type` and `email` are disabled; patchValue skips disabled controls.
    this.providerForm.get('type')?.setValue(this.providerData.type ?? null, { emitEvent: false });
    this.providerForm.get('email')?.setValue(this.providerData.email || '', { emitEvent: false });

    if (this.ownerDetails) {
      const personalAddress = (this.ownerDetails.personalAddress || {}) as Address;

      this.providerForm.patchValue({
        businessPhone: this.normalizeUsPhoneFormatting(this.providerData.phone),
        businessSecondaryPhone: this.normalizeUsPhoneFormatting(
          this.providerData.secondaryPhone || this.providerData.phonePrimary
        ),
        graduationDate: this.toFormDate(this.ownerDetails.graduationDate),
        schoolId: this.resolveOwnerSchoolId() ?? '',
        schoolCode: this.resolveOwnerSchoolCode() ?? '',
        schoolName: this.resolveOwnerSchoolName() || '',
        personalAddress1: personalAddress.address1 || '',
        personalAddress2: personalAddress.address2 || '',
        personalCity: personalAddress.city || '',
        personalState: this.getSelectedPersonalAddressState() || '',
        personalZip: personalAddress.zip || '',
        personalCounty: personalAddress.county || '',
        birthDate: this.toFormDate(this.ownerDetails.birthDate),
        licenseNumber: this.ownerDetails.licenseNumber || this.providerData.licenseNumber || '',
        npiNumber: this.ownerDetails.npiNumber || this.providerData.npiNumber || '',
        npiNumber2: this.ownerDetails.npiNumber2 || this.providerData.npiNumber2 || '',
        issuingState: this.getSelectedIssuingState() || '',
        licenseType: this.ownerDetails.licenseType || '',
        therapyLicenseTypeId: this.ownerDetails.therapyLicenseTypeId ?? 1,
        socialSecurityLast4: this.ownerDetails?.ssn || ''
      });
    }

    const state = this.providerForm.get('state')?.value;
    const personalState = this.providerForm.get('personalState')?.value;

    const county = this.providerForm.get('county');
    const personalCounty = this.providerForm.get('personalCounty');

    if (state?.code === 'NC') {
      county?.setValidators([Validators.required, noWhitespaceValidator()]);
    } else {
      county?.clearValidators();
    }

    if (personalState?.code === 'NC') {
      personalCounty?.setValidators([Validators.required, noWhitespaceValidator()]);
    } else {
      personalCounty?.clearValidators();
    }

    county?.updateValueAndValidity();
    personalCounty?.updateValueAndValidity();

    // HACK: I don't understand why this doesn't work without this line.
    if (!this.showReviewStatus) {
      this.providerForm.get('socialSecurityLast4')?.enable({ emitEvent: false });
    }
    this.providerForm.get('socialSecurityLast4')?.setValue(this.ownerDetails?.ssn, {
      emitEvent: false
    });

    this.applyTherapyFieldRules();
  }

  private toFormDate(value: unknown): string | null {
    const formatted = value ? this.formatDate(String(value)) : 'N/A';
    return formatted === 'N/A' ? null : formatted;
  }

  private asFormString(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    return String(value).trim();
  }

  private subjectsValueForForm(subjects: string[] | null | undefined): string | string[] {
    const names = this.normalizeSubjectNames(subjects);
    if (this.isTherapyProvider()) {
      return names[0] ?? '';
    }
    return names;
  }

  onEditClick(): void {
    this.edit.emit();
  }

  onProviderSaveClick(): void {
    if (this.providerForm.invalid) {
      this.revealAllValidationErrors(this.providerForm);
      return;
    }

    const formValue = this.providerForm.getRawValue();
    const persistLicense = this.persistLicenseFields(formValue.type);
    const licenseNumber = persistLicense ? this.asFormString(formValue.licenseNumber) : undefined;
    const issuingState = persistLicense ? formValue.issuingState : undefined;

    const prevOwner = this.providerData.ownerDetails ?? {};
    const prevPersonal = (prevOwner.personalAddress ?? {}) as Address;
    const existingSsn = prevOwner.ssn ?? null;
    const fullSsn = resolveSsnForSubmit(formValue.socialSecurityLast4, {
      isEditMode: !!existingSsn,
      existingValue: existingSsn
    });

    const next: ProviderData = {
      ...this.providerData,
      firstName: formValue.firstName,
      middleName: formValue.middleName,
      lastName: formValue.lastName,
      type: formValue.type,
      subjects: this.normalizeSubjectNames(formValue.subjects),
      businessName: formValue.businessName,
      email: formValue.email ?? '',
      businessEmail: formValue.businessEmail ?? '',
      phone: this.showReviewStatus ? formValue.phone : formValue.businessPhone,
      secondaryPhone: this.showReviewStatus ? formValue.secondaryPhone : formValue.businessSecondaryPhone,
      ...(this.showReviewStatus ? {} : { phonePrimary: formValue.businessSecondaryPhone }),
      npiNumber: this.asFormString(formValue.npiNumber),
      npiNumber2: this.asFormString(formValue.npiNumber2),
      licenseNumber,
      issuingState,
      ssn: fullSsn,
      addresses: [
        {
          ...this.providerData.addresses?.[0],
          address1: formValue.address1,
          address2: formValue.address2 || '',
          city: formValue.city,
          state: formValue.state?.code || formValue.state,
          zip: formValue.zip,
          county: formValue.county ?? ''
        }
      ],
      ownerDetails: {
        ...prevOwner,
        ...(this.showReviewStatus
          ? {}
          : {
              phone: formValue.phone,
              secondaryPhone: formValue.secondaryPhone
            }),
        firstName: formValue.firstName,
        middleName: formValue.middleName,
        lastName: formValue.lastName,
        phone: formValue.phone,
        secondaryPhone: formValue.secondaryPhone,
        schoolId: this.resolveOwnerSchoolId() ?? null,
        schoolCode: this.resolveOwnerSchoolCode() ?? null,
        schoolName: this.resolveOwnerSchoolName() ?? null,
        graduationDate: formValue.graduationDate,
        birthDate: formValue.birthDate,
        npiNumber: this.asFormString(formValue.npiNumber),
        npiNumber2: this.asFormString(formValue.npiNumber2),
        licenseNumber,
        licenseType: formValue.licenseType,
        therapyLicenseTypeId: formValue.therapyLicenseTypeId,
        issuingState,
        ssn: fullSsn,
        email: formValue.email,
        personalAddress: {
          ...prevPersonal,
          address1: formValue.personalAddress1 ?? '',
          address2: formValue.personalAddress2 ?? '',
          city: formValue.personalCity ?? '',
          state: formValue.personalState?.code || formValue.personalState,
          zip: formValue.personalZip ?? '',
          county: formValue.personalCounty ?? ''
        } as OwnerDetails['personalAddress']
      },
      enrollmentStatus:
        this.providerEnrollmentStatuses.find((s) => s.id === formValue.providerEnrollmentStatusId)?.name.replace(/\s+/g, '') ??
        this.providerData.enrollmentStatus,
    };
    this.providerData = next;

    this.save.emit(this.providerData);
  }

  onCancelClick(): void {
    this.cancelled.emit();
  }

  getTherapyEmployeeLength(employees: Employee[]): number {
    if (employees) {
      return employees.filter((employee) => employee.employeeType === 'Therapy').length ?? null;
    } else {
      return 0;
    }
  }

  getTutoringEmployeeLength(employees: Employee[]): number {
    if (employees) {
      return employees.filter((employee) => employee.employeeType === 'Tutor').length ?? null;
    } else {
      return 0;
    }
  }

  getSelectedBusinessAddressState(): State | null {
    return this.resolveState(this.providerData?.addresses?.[0]?.state ?? undefined);
  }

  getSelectedPersonalAddressState(): State | null {
    const personalAddress = (this.ownerDetails?.personalAddress || {}) as Address;
    return this.resolveState(personalAddress.state ?? undefined);
  }

  getSelectedIssuingState(): State | null {
    return this.resolveState(this.ownerDetails?.issuingState ?? this.providerData?.issuingState);
  }

  private resolveOwnerSchoolId(): string | number | null | undefined {
    if (this.ownerDetails?.schoolId != null && this.ownerDetails.schoolId !== '') {
      return this.ownerDetails.schoolId;
    }

    return this.getOwnerEmployee()?.schoolId ?? this.getOwnerEmployee()?.employeeSchool?.id;
  }

  private resolveOwnerSchoolName(): string {
    if (this.ownerDetails?.schoolName) {
      return this.ownerDetails.schoolName;
    }

    const ownerEmployee = this.getOwnerEmployee();
    return (
      ownerEmployee?.schoolName ??
      ownerEmployee?.employeeSchool?.schoolName ??
      ownerEmployee?.employeeSchool?.name ??
      ''
    );
  }

  private resolveOwnerSchoolCode(): string | null | undefined {
    if (this.ownerDetails?.schoolCode != null && this.ownerDetails.schoolCode !== '') {
      return this.ownerDetails.schoolCode;
    }

    return this.getOwnerEmployee()?.schoolCode ?? this.getOwnerEmployee()?.employeeSchool?.schoolCode ?? null;
  }

  private getOwnerEmployee(): Employee | undefined {
    const employees = this.providerData?.employees;
    if (!employees?.length) {
      return undefined;
    }
    return employees.find((employee) => employee.isOwner === true || employee.isOwner === 'true');
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';

    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
      }

      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return 'N/A';

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      return `${month}/${day}/${year}`;
    } catch {
      return 'N/A';
    }
  }

  private digitsOnly(value: unknown): string {
    const normalizeToDigits = (input: unknown): string => {
      if (typeof input === 'string' || typeof input === 'number' || typeof input === 'bigint') {
        return String(input).replace(/\D/g, '');
      }
      return '';
    };

    if (value != null && typeof value === 'object' && 'phoneNumber' in value) {
      return normalizeToDigits((value as { phoneNumber?: unknown }).phoneNumber);
    }

    return normalizeToDigits(value);
  }

  private asDisplayString(value: unknown): string {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return String(value);
    }
    return '';
  }

  private normalizeUsPhoneFormatting(value: unknown): string {
    const digits = this.digitsOnly(value);
    if (!digits) return '';
    const ten = digits.length > 10 ? digits.slice(-10) : digits;
    if (ten.length !== 10) {
      return this.asDisplayString(value);
    }
    return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  }

  private formatFeinForDisplay(value: unknown): string {
    return this.digitsOnly(value);
  }

  onStateChange(event: MatSelectChange): void {
    this.providerForm.patchValue({ state: event.value });
  }

  onSchoolStateChange(event: MatSelectChange): void {
    this.schoolForm.patchValue({ schoolState: event.value });
  }

  updateSchoolFormValues(): void {
    if (!this.schoolForm || !this.schoolData) return;

    const address = this.schoolData.addresses?.[0];

    let hdmaFirstName = this.schoolData.hdmaFirstName || '';
    let hdmaLastName = this.schoolData.hdmaLastName || '';
    if (!hdmaFirstName && !hdmaLastName && this.schoolData.hdmaName) {
      const nameParts = this.schoolData.hdmaName.trim().split(' ');
      hdmaFirstName = nameParts[0] || '';
      hdmaLastName = nameParts.slice(1).join(' ') || '';
    }

    let bgCheckDate: Date | null = null;
    if (this.schoolData.backgroundCheckValidationDate) {
      const parsedDate = new Date(this.schoolData.backgroundCheckValidationDate);
      if (!Number.isNaN(parsedDate.getTime())) {
        bgCheckDate = parsedDate;
      }
    }

    this.schoolForm.patchValue({
      hdmaFirstName: hdmaFirstName,
      hdmaLastName: hdmaLastName,
      hdmaMiddleName: this.schoolData.hdmaMiddleName || '',
      hdmaEmail: this.schoolData.hdmaEmail || this.schoolData.email || '',
      schoolPhone: this.normalizeUsPhoneFormatting(this.schoolData.phone),
      schoolPhoneExt: this.getSchoolPhoneExtension(),
      backgroundCheckValidationDate: bgCheckDate,
      schoolId: this.schoolData.schoolId || this.schoolData.recordNumber || '',
      schoolName: this.schoolData.schoolName || '',
      schoolStatusId: this.schoolData.schoolStatusId ?? null,
      fiscalYearEndDate: this.parseDateValue(this.schoolData.fiscalYearEndDate),
      educationDeliveryMethod: this.schoolData.educationDeliveryMethod
        ? this.schoolData.educationDeliveryMethod.split(',').map((v) => (v.trim() === 'Remote/Virtual' ? 1 : 2))
        : [],
      aidProgramEligibility: parseAidProgramEligibility(this.schoolData.aidProgramEligibility),
      feinNumber: this.formatFeinForDisplay(this.schoolData.federalEmployerID || this.schoolData.feinNumber || ''),
      dnpeNumber: this.schoolData.dnpeNumber || '',
      schoolTypeId: this.schoolData.schoolTypeId ?? null,
      schoolAddress1: address.address1 || '',
      schoolAddress2: address.address2 || '',
      schoolCity: address.city || '',
      schoolState: this.getSelectedSchoolAddressState() || '',
      schoolCounty: address.county || '',
      schoolZip: address.zip || ''
    });

    const state = this.schoolForm.get('schoolState')?.value;
    const county = this.schoolForm.get('schoolCounty');

    if (state?.code === 'NC') {
      county?.setValidators([Validators.required, noWhitespaceValidator()]);
    } else {
      county?.clearValidators();
    }

    county?.updateValueAndValidity();
  }

  public get institutionSchoolCount(): number {
    return this.schoolData?.institutionSchoolCount ?? 0;
  }

  parseDateValue(dateString: string): Date | null {
    if (!dateString) return null;
    const parsedDate = new Date(dateString);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  getSelectedSchoolAddressState(): State | null {
    const address = this.schoolData?.addresses?.[0];
    if (!address?.state) {
      return null;
    }
    const stateCode = address.state;
    return this.states.find((state) => state.code === stateCode) || null;
  }

  private getSchoolPhoneExtension(): string {
    const personPhone = this.schoolData?.hdmaContact?.person?.personPhones?.[0];
    return (personPhone?.phone?.extension ?? personPhone?.extension ?? '').toString();
  }

  onSchoolSaveClick(): void {
    if (this.schoolForm.invalid) {
      this.schoolForm.markAllAsTouched();
      return;
    }

    const formValue = this.schoolForm.getRawValue();

    let bgCheckDateFormatted = formValue.backgroundCheckValidationDate;
    if (bgCheckDateFormatted instanceof Date) {
      bgCheckDateFormatted = bgCheckDateFormatted.toISOString();
    }

    const addressData = {
      address1: formValue.schoolAddress1,
      address2: formValue.schoolAddress2,
      city: formValue.schoolCity,
      state: formValue.schoolState?.code || formValue.schoolState,
      county: formValue.schoolCounty,
      zip: formValue.schoolZip,
      description: null,
      id: this.schoolData.businessAddressId
    };

    const payload = {
      ...this.schoolData,
      fiscalYearEndDate: formValue.fiscalYearEndDate,
      hdmaFirstName: formValue.hdmaFirstName,
      hdmaMiddleName: formValue.hdmaMiddleName,
      hdmaLastName: formValue.hdmaLastName,
      phone: this.digitsOnly(formValue.schoolPhone),
      hdmaContact: {
        ...this.schoolData.hdmaContact,
        person: {
          ...this.schoolData.hdmaContact?.person,
          firstName: formValue.hdmaFirstName,
          middleName: formValue.hdmaMiddleName,
          lastName: formValue.hdmaLastName,
          personPhones: [
            {
              ...this.schoolData.hdmaContact?.person?.personPhones?.[0],
              extension: formValue.schoolPhoneExt || null,
              phone: {
                ...this.schoolData.hdmaContact?.person?.personPhones?.[0]?.phone,
                phoneNumber: this.digitsOnly(formValue.schoolPhone),
                extension: formValue.schoolPhoneExt || null
              }
            }
          ]
        }
      },
      backgroundCheckValidationDate: bgCheckDateFormatted,
      schoolName: formValue.schoolName,
      schoolStatusId: formValue.schoolStatusId,
      educationDeliveryMethod: formValue.educationDeliveryMethod,
      aidProgramEligibility: Array.isArray(formValue.aidProgramEligibility)
        ? formValue.aidProgramEligibility
        : [],
      federalEmployerID: this.digitsOnly(formValue.feinNumber),
      feinNumber: this.digitsOnly(formValue.feinNumber),
      dnpeNumber: formValue.dnpeNumber,
      schoolTypeId: formValue.schoolTypeId
    } as SchoolRecordVm;

    if (this.schoolData.addresses) {
      payload.addresses = [
        {
          ...this.schoolData.addresses[0],
          ...addressData
        }
      ];
    } else {
      payload.addresses = [addressData];
    }

    this.save.emit(payload);
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case ProviderInfoStatus.InReview:
        return 'in-review';
      case ProviderInfoStatus.Approved:
        return 'approved';
      case ProviderInfoStatus.Rejected:
      case ProviderInfoStatus.ClassWallet:
        return 'rejected';
      case ProviderInfoStatus.InPerson:
        return 'in-person';
      default:
        return 'in-review';
    }
  }

  updateHouseholdFormValues(): void {
    if (!this.householdForm || !this.householdData) return;
    if (this.householdForm.dirty) return;

    const address = this.householdData.addresses?.[0] || {
      address1: this.householdData.address1,
      address2: this.householdData.address2,
      city: this.householdData.city,
      state: this.householdData.state,
      zip: this.householdData.zip,
      county: this.householdData.county
    };

    this.householdForm.patchValue({
      // Contact Information
      address1: address.address1 || '',
      address2: address.address2 || '',
      city: address.city || '',
      state: this.getSelectedHouseholdState(),
      zip: address.zip || '',
      county: address.county || '',
      phone: this.normalizeUsPhoneFormatting(this.householdData.phoneNumber),
      email: this.householdData.email || '',

      // Parent Information
      firstName: this.householdData.firstName || '',
      middleName: this.householdData.middleName || '',
      lastName: this.householdData.lastName || '',
      birthDate: this.householdData.dob
        ? (() => {
            const [y, m, d] = this.householdData.dob.split('T')[0].split('-').map(Number);
            return new Date(y, m - 1, d);
          })()
        : null,
      ssn: this.householdData.ssn || '',

      // Household Information
      householdIncome: this.householdData.householdIncome ?? '',
      householdMemberCount: this.householdData.householdMemberCount || '',
      householdIncomeVerification: this.householdData.incomeVerificationRequired ?? false,
      householdNcResidency: this.householdData.ncResidency ?? NOT_EVALUATED,
      householdNcResidencyVerified: this.householdData.ncResidencyVerified ?? NOT_EVALUATED,

      // Read-only fields
      pidNumber: this.householdData.pidNumber || '',
      householdTier: this.householdData.householdTier || '',
      // Blank until Income Verification completes.
      verifiedIncome: this.formatVerifiedIncome(this.householdData.verifiedIncome)
    });

    // A household that declined to disclose income is out of scope for Income Verification, so the
    // control is locked rather than letting an admin request a verification the API will refuse.
    // The form outlives a change of household, so this has to reset both ways.
    const incomeVerificationControl = this.householdForm.get('householdIncomeVerification');
    if (this.householdData.disclosedInformation === false) {
      incomeVerificationControl?.disable({ emitEvent: false });
    } else if (this.currentUserType !== AdminUserType.Households) {
      incomeVerificationControl?.enable({ emitEvent: false });
    }
  }

  // Renders as an empty control until the Income Verification process writes a value, which is what
  // distinguishes "not yet verified" from a genuine $0 verified income.
  private formatVerifiedIncome(verifiedIncome: number | null | undefined): string {
    if (verifiedIncome == null) return '';

    return `$${verifiedIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  getSelectedHouseholdState(): State | null {
    const stateValue = this.householdData?.addresses?.[0]?.state || this.householdData?.state;

    if (!stateValue) return null;

    return this.resolveState(stateValue);
  }

  onHouseholdStateChange(event: MatSelectChange): void {
    this.householdForm.patchValue({ state: event.value });
  }

  onHouseholdSaveClick(): void {
    if (this.householdForm.invalid) {
      this.householdForm.markAllAsTouched();
      return;
    }

    const formValue = this.householdForm.getRawValue();

    let dobFormatted: string | null = null;
    if (formValue.birthDate) {
      const date = new Date(formValue.birthDate);
      dobFormatted = Number.isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
    }

    const payload = {
      id: this.householdData.id,
      householdAccountId: this.householdData.householdAccountId,
      personId: this.householdData.personId,
      programPartnerId: this.householdData.programPartnerId ?? null,

      firstName: formValue.firstName,
      middleName: formValue.middleName || '',
      lastName: formValue.lastName,
      dob: dobFormatted,
      phoneNumber: formValue.phone ? formValue.phone.replace(/\D/g, '') : '',

      ssn: formValue.ssn ? formValue.ssn.replace(/\D/g, '') : '',

      CountryCode: 1,
      PhoneTypeId: 2,

      householdIncome: formValue.householdIncome === '' || formValue.householdIncome == null
        ? null : Number(formValue.householdIncome),
      householdMemberCount: formValue.householdMemberCount === '' || formValue.householdMemberCount == null
        ? null : Number(formValue.householdMemberCount),
      incomeVerificationRequired: Boolean(formValue.householdIncomeVerification),
      // The NOT_EVALUATED sentinel (any non-boolean) maps back to null so the API clears residency.
      ncResidency: typeof formValue.householdNcResidency === 'boolean' ? formValue.householdNcResidency : null,
      ncResidencyVerified:
        typeof formValue.householdNcResidencyVerified === 'boolean' ? formValue.householdNcResidencyVerified : null,

      address1: formValue.address1,
      address2: formValue.address2,
      city: formValue.city,
      state: formValue.state?.code || formValue.state,
      county: formValue.county,
      zip: formValue.zip,

      prefix: this.householdData.prefix,
      suffix: this.householdData.suffix
    };

    this.save.emit(payload);
  }

  applyCountyValidation(form: FormGroup, stateControlName: string, countyControlName: string): void {
    form
      .get(stateControlName)
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        const county = form.get(countyControlName);
        let stateCode: string | undefined;

        if (typeof state === 'object' && state !== null && 'code' in state && (state as { code?: string }).code) {
          stateCode = (state as { code: string }).code;
        } else if (typeof state === 'string') {
          const matched = this.states.find((stateObj) => stateObj.code === state || stateObj.name === state);
          stateCode = matched?.code;
        }

        if (stateCode === 'NC') {
          county?.setValidators([Validators.required, noWhitespaceValidator()]);
          county?.markAsTouched();
        } else {
          county?.clearValidators();
          county?.setErrors(null);
          if (stateCode) {
            county?.setValue('');
          }
        }

        county?.updateValueAndValidity();
        form.updateValueAndValidity();
      });
  }

  private resolveState(stateValue?: string | AddressState | null): State | null {
    if (stateValue == null || stateValue === '') return null;

    if (typeof stateValue === 'object') {
      const obj = stateValue as { code?: string; name?: string };
      if (obj.code) {
        const byCode = this.states.find((state) => state.code === obj.code);
        if (byCode) return byCode;
      }
      if (obj.name) {
        return this.states.find((state) => state.code === obj.name || state.name === obj.name) || null;
      }
      return null;
    }

    return this.states.find((state) => state.code === stateValue || state.name === stateValue) || null;
  }

  getFullName(): string {
    return (
      [this.householdData?.firstName, this.householdData?.middleName || '', this.householdData?.lastName]
        .filter(Boolean)
        .join(' ') || '-'
    );
  }
}
